import { itsutsuNoKata } from './itsutsu-no-kata'
import { juNoKata } from './ju-no-kata'
import { katameNoKata } from './katame-no-kata'
import { kimeNoKata } from './kime-no-kata'
import { koshikiNoKata } from './koshiki-no-kata'
import { kodokanGoshinJutsu } from './kodokan-goshin-jutsu'
import { nageNoKata } from './nage-no-kata'
import type { KataDefinition, KataId, Technique, TechniqueGroup } from './types'
import { flattenTechniques } from './types'

/** All seven kata in UI/UX display order. */
export const catalog: KataDefinition[] = [
  nageNoKata,
  katameNoKata,
  kimeNoKata,
  juNoKata,
  kodokanGoshinJutsu,
  itsutsuNoKata,
  koshikiNoKata,
]

export function getKata(id: KataId): KataDefinition | undefined {
  return catalog.find((k) => k.id === id)
}

/** Category selector only when a kata has more than one group. */
export function usesCategorySelector(kata: KataDefinition): boolean {
  return kata.groups.length > 1
}

export function findGroup(
  kata: KataDefinition,
  techniqueId: string,
): TechniqueGroup | undefined {
  return kata.groups.find((g) => g.techniques.some((t) => t.id === techniqueId))
}

/**
 * Navigation scope: within the active category when categories apply;
 * otherwise the full kata technique list.
 */
export function navigationTechniques(
  kata: KataDefinition,
  categoryId: string | null,
): Technique[] {
  if (!usesCategorySelector(kata)) {
    return flattenTechniques(kata)
  }
  const group = kata.groups.find((g) => g.id === categoryId) ?? kata.groups[0]
  return group?.techniques ?? []
}

function normalizeSearchTerm(value: string): string {
  return value.trim().toLowerCase().replace(/[-_\s]+/g, ' ')
}

export function searchTechniques(query: string): { kata: KataDefinition; technique: Technique }[] {
  const normalized = normalizeSearchTerm(query)
  if (!normalized) return []
  const hits: { kata: KataDefinition; technique: Technique }[] = []
  for (const kata of catalog) {
    for (const technique of flattenTechniques(kata)) {
      const names = [
        technique.name,
        technique.nameJa,
        ...(technique.nameZh ? [technique.nameZh] : []),
        ...(technique.searchAliases ?? []),
      ]
      if (names.some((n) => normalizeSearchTerm(n).includes(normalized))) {
        hits.push({ kata, technique })
      }
    }
  }
  return hits
}

export type {
  KataDefinition,
  KataId,
  KataTheme,
  Technique,
  TechniqueGroup,
} from './types'
export { findTechnique, flattenTechniques } from './types'
export {
  techniqueDisplayName,
  techniqueScriptLang,
  techniqueScriptName,
} from './labels'
export {
  nageNoKata,
  katameNoKata,
  kimeNoKata,
  juNoKata,
  kodokanGoshinJutsu,
  itsutsuNoKata,
  koshikiNoKata,
}
